import React from 'react';
import create from 'zustand';
import createContext from 'zustand/context';

type PasswordContext = {
  needsPassword: boolean;
  password: string;
};
const { Provider, useStore } = createContext<PasswordContext>();

type PasswordContextActions = {
  setNeedsPassword: (needsPassword: boolean) => void;
  setPassword: (password: string) => void;
};

const createStore =
  (initialState: Omit<PasswordContext, keyof PasswordContextActions>) => () =>
    create<PasswordContext>(set => ({
      ...initialState,
      setPassword: (password: string) =>
        set((state: PasswordContext) => ({ ...state, password })),
      setNeedsPassword: (needsPassword: boolean) =>
        set((state: PasswordContext) => ({ ...state, needsPassword })),
    }));

type PasswordContextProps = {
  children: React.ReactElement;
  contextValue: PasswordContext;
};

export function PasswordContextProvider({
  children,
  contextValue,
}: PasswordContextProps): JSX.Element {
  return (
    <Provider createStore={createStore(contextValue)}>{children}</Provider>
  );
}

export function usePasswordContext(): PasswordContext & PasswordContextActions {
  const context = useStore() as PasswordContext & PasswordContextActions;

  if (context === undefined) {
    throw new Error(
      'usePasswordContext must be used within a PasswordContextProvider'
    );
  }

  return context;
}
